/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useMemo, useState } from "react";
import { usePaymentProcessorSelection } from "./usePaymentProcessorSelection";
import { usePaymentMethods } from "../../lib/react-query/usePaymentMethods";
import { useInvoiceRebill } from "../../lib/react-query/useInvoiceRebill";
import { useConfig } from "../../lib/react-query/useConfig";
import { useAuth } from "../useAuth";
import { useToast } from "../../components/ui/Toast";
import { INVOICE_STATUS, TOAST_ERROR_MESSAGE } from "../../constants/constants";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const DECLINED_MESSAGE = "The payment was declined. Please pay with another payment method.";

/**
 * Processors, stored methods and the save opt-in as one choice: `hasSelection`
 * gates Proceed, and the caller pays with whichever side is selected.
 */
export const usePaymentSelection = (
  invoice,
  paymentProcessors,
  { disabledPayLater = false, hideDelay = false, recurringOnly = false, recurringParams = {} } = {}
) => {
  const { data: config } = useConfig();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  const [methodSelection, setSelectedMethodId] = useState(null);
  const [saveMethod, setSaveMethod] = useState(false);
  // Rebill only accepts `new`/`pending` invoices, so stored methods are withdrawn for
  // any other status (error, cancelled, ...). A decline leaves the invoice in `error`;
  // `rebillDeclined` covers that until the invoice prop is refetched.
  // A pay-later invoice is `paid` without `payment_received`; the server reads that as `pending`.
  const [rebillDeclined, setRebillDeclined] = useState(false);
  const isAwaitingPayment = invoice?.status === INVOICE_STATUS.PAID && !invoice.payment_received;
  const rebillRejected =
    rebillDeclined ||
    (!!invoice?.status &&
      !isAwaitingPayment &&
      ![INVOICE_STATUS.NEW, INVOICE_STATUS.PENDING].includes(invoice.status));

  const rebillingEnabled = !!config?.allow_rebilling_payments && isAuthenticated;

  const {
    selectedProcessor: processorSelection,
    setSelectedProcessor,
    filteredPaymentProcessors,
  } = usePaymentProcessorSelection(paymentProcessors, disabledPayLater, { hideDelay, recurringOnly });

  const { data: paymentMethods, isLoading: isLoadingSavedMethods } = usePaymentMethods({ enabled: rebillingEnabled });

  const savedMethods = useMemo(
    () =>
      (paymentMethods || []).filter(
        (method) => method.is_active && (!recurringOnly || method.is_recurring_supported)
      ),
    [paymentMethods, recurringOnly]
  );
  const showSavedMethods = rebillingEnabled && !rebillRejected && savedMethods.length > 0;

  // A selected method that drops out of the list (e.g. auto-renew ticked and it has no
  // recurring support) no longer counts as selected.
  const selectedMethodId =
    showSavedMethods && savedMethods.some((method) => method.id === methodSelection) ? methodSelection : null;

  // usePaymentProcessorSelection re-auto-selects a lone processor at any time;
  // masking keeps "one of the two, never both" an invariant.
  const selectedProcessor = selectedMethodId ? null : processorSelection;

  const { mutate: rebill, isPending: isPaying } = useInvoiceRebill(invoice?.id);

  const selectProcessor = useCallback((id) => {
    setSelectedProcessor(id);
    setSelectedMethodId(null);
  }, [setSelectedProcessor]);

  const selectSavedMethod = useCallback((id) => {
    setSelectedMethodId(id);
    setSelectedProcessor(null);
  }, [setSelectedProcessor]);

  const handleDecline = useCallback(() => {
    toast({ title: "Error!", description: DECLINED_MESSAGE, variant: "error" });
    setSelectedMethodId(null);
    setRebillDeclined(true);
  }, [toast]);

  // A decline still answers 200, so success is read off the returned status.
  const payWithSavedMethod = useCallback(
    (onPaid) => {
      rebill(
        { paymentMethodId: selectedMethodId, recurring: recurringParams.recurring },
        {
          onSuccess: (paidInvoice) => {
            if (paidInvoice?.status === INVOICE_STATUS.ERROR) {
              handleDecline();
              return;
            }
            onPaid(paidInvoice);
          },
          onError: (error) => {
            toast({
              title: "Error!",
              description: extractErrorMessage(error, TOAST_ERROR_MESSAGE.DEFAULT),
              variant: "error",
            });
          },
        }
      );
    },
    [rebill, selectedMethodId, recurringParams.recurring, handleDecline, toast]
  );

  const selectedProcessorSupportsRebilling =
    !!selectedProcessor &&
    !!filteredPaymentProcessors?.find((processor) => processor.id === selectedProcessor)
      ?.is_rebilling_supported;

  // Mirrors the server's 400s; convenience, not enforcement.
  const canSaveMethod = rebillingEnabled && selectedProcessorSupportsRebilling;

  return {
    // stored payment methods
    savedMethods,
    showSavedMethods,
    selectedMethodId,
    selectSavedMethod,
    payWithSavedMethod,
    isPaying,
    isLoadingSavedMethods,

    // new payment method
    filteredPaymentProcessors,
    selectedProcessor,
    selectProcessor,

    // storing the new payment method
    canSaveMethod,
    saveMethod,
    setSaveMethod,
    storeParams: canSaveMethod && saveMethod ? { store_payment_method: 1 } : {},

    hasSelection: !!selectedProcessor || !!selectedMethodId,
  };
};
